/**
 * Date helpers for the Förvaltning calendar view. Dates are plain
 * `YYYY-MM-DD` day keys in the browser's local time, like the rest of the board
 * (stored dates are Stockholm strings `YYYY-MM-DD hh:mm:ss`; their first ten
 * characters are the day).
 */

export interface CalendarMonth {
    year: number;
    /** 0-based, like Date#getMonth. */
    month: number;
}

export interface CalendarDay {
    date: string;
    dayOfMonth: number;
    inMonth: boolean;
}

export interface CalendarWeek {
    weekNumber: number;
    days: CalendarDay[];
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DD` of a local Date. */
export function toDayKey(date: Date): string {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The day part of a stored date (`YYYY-MM-DD hh:mm:ss` or `YYYY-MM-DD`), or null. */
export function dayKeyOf(stored?: string | null): string | null {
    if (!stored || !/^\d{4}-\d{2}-\d{2}/.test(stored)) return null;
    return stored.slice(0, 10);
}

export function monthOf(date: Date): CalendarMonth {
    return { year: date.getFullYear(), month: date.getMonth() };
}

export function addMonths({ year, month }: CalendarMonth, delta: number): CalendarMonth {
    const total = year * 12 + month + delta;
    return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

export function sameMonth(a: CalendarMonth, b: CalendarMonth): boolean {
    return a.year === b.year && a.month === b.month;
}

/** First day of the month as a day key, e.g. `2026-10-01`. */
export function monthStartKey({ year, month }: CalendarMonth): string {
    return `${year}-${pad(month + 1)}-01`;
}

/** Whether a day key falls in the month. */
export function isInMonth(dayKey: string, { year, month }: CalendarMonth): boolean {
    return dayKey.startsWith(`${year}-${pad(month + 1)}-`);
}

/** ISO 8601 week number (weeks start Monday; week 1 holds the first Thursday). */
export function isoWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/** The month as whole Monday–Sunday weeks, padded with the neighbouring months' days. */
export function monthWeeks({ year, month }: CalendarMonth): CalendarWeek[] {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7; // days since Monday
    const cursor = new Date(year, month, 1 - offset);
    const weeks: CalendarWeek[] = [];
    do {
        const days: CalendarDay[] = [];
        const weekNumber = isoWeekNumber(cursor);
        for (let i = 0; i < 7; i++) {
            days.push({ date: toDayKey(cursor), dayOfMonth: cursor.getDate(), inMonth: cursor.getMonth() === month });
            cursor.setDate(cursor.getDate() + 1);
        }
        weeks.push({ weekNumber, days });
    } while (cursor.getMonth() === month);
    return weeks;
}

/** "oktober 2026" */
export function monthLabel({ year, month }: CalendarMonth): string {
    return new Date(year, month, 1).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });
}

/** "ons 22 okt" */
export function dayLabel(dayKey: string): string {
    const [y, m, d] = dayKey.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Group items by day key, keeping input order within a day. Items without a day are dropped. */
export function groupByDay<T>(items: T[], dayOf: (item: T) => string | null): Map<string, T[]> {
    const map = new Map<string, T[]>();
    for (const item of items) {
        const day = dayOf(item);
        if (!day) continue;
        const list = map.get(day);
        if (list) list.push(item);
        else map.set(day, [item]);
    }
    return map;
}
